import { axios } from "@/lib/axios";
import type { AxiosResponse } from "axios";

// Types for apiSearchEventList
export interface SearchGuessItem {
    id: number;
    guessNo: string;
    title: string;
    imageUrl: string;
    yesInitPrice: string;
    noInitPrice: string;
    yesTradeAmount: string;
    noTradeAmount: string;
    yesReserveShare: number;
    noReserveShare: number;
    yesRealtimePrice: string;
    noRealtimePrice: string;
    createTime: number;
    updateTime: number;
}

export interface SearchEventItem {
    id: number;
    eventNo: string;
    title: string;
    overview: string;
    imageUrl: string;
    topicIds: number[];
    primaryTopicId: number;
    topicNames: string[];
    primaryTopicName: string;
    liveState: number;
    hotFlag: number;
    recommendFlag: number;
    bonusEnabled?: boolean | number;
    occurTime: number;
    /**
     * Legacy fields consumed by EventCard. Some live/search endpoints no longer return them,
     * so callers should map publishTime/endTime to these values when needed.
     */
    acceptStartTime?: number;
    acceptEndTime?: number;
    endTime: number;
    resultBasis: string;
    sort: number;
    createTime: number;
    updateTime: number;
    guessList: SearchGuessItem[];
    source?: string;
    betLimit?: number;
    publishTime?: number;
}

export interface SearchEventListResponse {
    current: number;
    size: number;
    total: number;
    pages: number;
    records: SearchEventItem[];
    searchResultEmpty: boolean;
}

export function apiGetEventDetail( eventNo: string ) {
    return axios.post( `/api/front/guest/event-detail`, { eventNo } );
}

export function apiGetGuessDetailByGuess( guessNo: string ) {
    return axios.post( `/api/front/guest/user/guess-detail`, { guessNo } );
}

export interface GuessGroupListItemResponse {
    groupTitle?: string;
    guessGroupNo?: string;
    guessNo?: string;
    guessTitle?: string;
    mainFlag?: boolean | number | string;
    title?: string;
    [property: string]: any;
}

export function apiGetGuessGroupList( params: { guessGroupNo: string } ) {
    return axios.get<{ data: GuessGroupListItemResponse[] }>( "/api/front/event/guess-group/list", { params } );
}

export function apiGetLiveTopicIds() {
    return axios.post( "/api/front/event/live-topic-ids" );
}

export function apiSearchEventList( params: {
    pageNum: number; // 当前页
    pageSize: number; // 分页大小
    overview: string; // 用来搜索事件的关键字
    sortBy: "all" | "closingSoon" | "volume"; // Trending
    frequency: "all" | "new" | "daily" | "weekly"; // Frequency
} ): Promise<AxiosResponse<{ data: SearchEventListResponse }>> {
    return axios.post( "/api/front/event/page/overview", {}, { params } );
}

// Reuse SearchEventItem type for live topic events (data structure is identical)
export function apiGetLiveTopicEvents( topicId: string | number ): Promise<AxiosResponse<{ data: SearchEventItem[] }>> {
    return axios.post( `/api/front/event/live-topic/${topicId}/events` );
}

export function apiGetLiveTodayEvents(
    params: { lastId?: number | string; size: number },
): Promise<AxiosResponse<{ data: SearchEventItem[] }>> {
    return axios.get( "/api/front/event/live/today", { params: { lastId: params.lastId, size: params.size } } );
}

export function apiGetUserGuessPosition( guessNo: string ) {
    return axios.post( "/api/guest/user-bet-share", { guessNo } );
}

export function apiGetGuessChartData( params: { guessNo: string; timeType: number } ) {
    return axios.post( "/api/front/guest/guess-price-chart", params );
}

type SportEventTimeRange = "past1w" | "past2w" | "past3w" | "upcoming" | "upcoming2w" | "upcoming3w";

export function apiGetSportEvents(
    params: { topicId: number; pageNum: number; pageSize: number; timeRange?: SportEventTimeRange },
) {
    return axios.get( "/api/front/event/sports/upcoming", { params } );
}

export function apiGetWorldCupSportEvents(
    params: { pageNum: number; pageSize: number; timeRange?: SportEventTimeRange },
) {
    return axios.get( "/api/front/event/sports/world-cup/upcoming", { params } );
}

export function apiGetSportsDetail( eventNo: string ) {
    return axios.get( `/api/front/event/sports-detail/${eventNo}` );
}

export interface FootballMatchStatusTeamVO {
    abbr: string | null;
    logo: string | null;
    name: string | null;
    teamId: string | null;
}

export interface FootballMatchStatusScoreVO {
    awayAggregate: number | null;
    awayHalf: number | null;
    awaySecondHalf: number | null;
    awayTotal: number | null;
    display: string | null;
    halfTimeDisplay: string | null;
    homeAggregate: number | null;
    homeHalf: number | null;
    homeSecondHalf: number | null;
    homeTotal: number | null;
}

export interface FootballMatchStatusTimeVO {
    clock: number | null;
    displayTime: string | null;
    isClockStopped: boolean | null;
    period: string | null;
}

export interface FootballMatchStatusDisplayStatusVO {
    displayStatus: string | null;
    displayText: string | null;
    rawPeriod: string | null;
    rawStatus: string | null;
}

export interface FootballMatchStatusStatVO {
    awayValue: number | null;
    code: string;
    homeValue: number | null;
    name: string;
    valueType: string;
}

export interface FootballMatchStatusVO {
    available: boolean;
    eventNo: string;
    fixtureId: string | null;
    lastCheckedAt: number | null;
    lastSuccessAt: number | null;
    lastSyncStatus: string | null;
    match: {
        leagueId: string | null;
        leagueName: string | null;
        rawStatus: string | null;
        sport: string | null;
        startDate: number | null;
    };
    score: FootballMatchStatusScoreVO;
    source: string | null;
    stale: boolean;
    stats: FootballMatchStatusStatVO[];
    status: FootballMatchStatusDisplayStatusVO;
    teams: { away: FootballMatchStatusTeamVO | null; home: FootballMatchStatusTeamVO | null };
    time: FootballMatchStatusTimeVO;
    unavailableReason: string | null;
}

export function apiGetFootballMatchStatus( eventNo: string ) {
    return axios.get<{ data: FootballMatchStatusVO }>( `/api/front/event/football/${eventNo}/match-status` );
}

export function apiGetEventCategory( eventNo: string ): Promise<AxiosResponse<{ data: number }>> {
    return axios.get( "/api/front/event/category", { params: { eventNo } } );
}
// 关注事件
export function apiEventFollow( eventNo ) {
    return axios.post( "/api/front/event/follow", { eventNo } );
}
// 取消事件
export function apiEventUnfollow( eventNo ) {
    return axios.post( "/api/front/event/unfollow", { eventNo } );
}
// 获取 用户关注的事件列表
export function apiGetUserFollowEvents( { pageNum, pageSize } ) {
    return axios.get( "/api/front/event/followed", { params: { pageNum, pageSize } } );
}

// 获取区块链详情
export function apiGetCryptoDetail( eventNo: string ) {
    return axios.get( `/api/front/event/crypto-detail/${eventNo}` );
}

// 获取区块链事件下的竞猜数据
export type PageCryptoEventGuessRequest = {
    /**
     * true:找历史 false:找未来
     */
    direction: boolean;
    /**
     * 事件编号
     */
    eventNo: string;
    /**
     * 偏移: id
     */
    id: number;
    /**
     * size
     */
    size?: number;
};
export function apiGetCryptoEventGuesses( data: PageCryptoEventGuessRequest ) {
    return axios.post( `/api/front/event/crypto/guess/page`, data );
}

export function apiGetCryptoPrice5sRecent( params: { symbolId: string | number; thirdType?: string | number } ) {
    return axios.get( "/api/front/event/crypto/price-5s/recent", { params } );
}

// ============================================================
// 板球实时比分面板相关类型定义和接口
// ============================================================

/** 球颜色枚举（由后端计算，前端直接映射样式）*/
export type BallColor = "GREEN" | "CYAN" | "PINK" | "GRAY" | "WHITE";

/** 球类型枚举 */
export type BallType = "NORMAL" | "FOUR" | "SIX" | "WIDE" | "NO_BALL" | "WICKET";

/** 单球数据 */
export interface CricketBallVO {
    /** 前端展示文字：wd / nb / W / 4 / 6 / 数字 */
    display: string;
    /** 是否为合法球（wide/no-ball 为 false，不计入正常6球计数）*/
    legal: boolean;
    /** 球类型 */
    type: BallType;
    /** 球颜色 */
    color: BallColor;
    /** 本球总得分（含附加分，用于 overRuns 累加和前端展示）*/
    runs: number;
}

/** 单个 Over 的球序列数据 */
export interface CricketOverBallsVO {
    /** 展示用 over 编号（= dbOver + 1，从1开始）*/
    displayOver: number;
    /** 展示标题，如 "Over 11" */
    title: string;
    /** 该 over 内合法球数（不含 wide/no-ball）*/
    legalBallCount: number;
    /** 该 over 总得分（所有球 runs 之和）*/
    overRuns: number;
    /** 是否为当前正在进行的 over */
    current: boolean;
    /** 该 over 的逐球列表 */
    balls: CricketBallVO[];
    /** 该 over 内 6 个合法球（wide/no-ball 不占合法球数） */
    legalBalls?: CricketBallVO[];
    /** 该 over 内额外球（wide/no-ball），分数已计入 overRuns */
    extraBalls?: CricketBallVO[];
}

/** 局信息 */
export interface CricketInningVO {
    /** 局编号，例如 Super Over 可能从 3 开始 */
    inningNo?: number;
    /** Super Over 轮次 */
    round?: number;
    /** 击球方球队简称，例如 "DC" */
    teamShortName: string;
    /** 击球方球队 logo URL */
    teamLogoUrl: string;
    /** 该局比分 */
    score?: {
        /** 展示字符串，如 "120-3" */
        display: string;
    } | null;
    /** 该局 over 进度 */
    over?: {
        /** 展示字符串，如 "10.2" */
        display: string;
    } | null;
    /** 该局所有 over 的逐球列表，按 over 升序 */
    overList: CricketOverBallsVO[];
}

/** 板球实时比分面板数据 */
export interface CricketLivePanelVO {
    /** 当前击球方球队信息 */
    battingTeam: {
        /** 球队简称，如 "DC" */
        shortName: string;
        /** 球队 logo URL */
        logoUrl: string;
    };
    /** 最终获胜球队 ID，比赛未完赛或暂无结果时为 null */
    winningTeamId?: string | null;
    /** 最终获胜球队信息，比赛未完赛或暂无结果时为 null */
    winningTeam?: {
        /** 球队 ID */
        teamId: string;
        /** 球队名称 */
        name: string;
        /** 球队简称，如 "DC" */
        shortName: string;
        /** 球队 logo URL */
        logoUrl: string;
    } | null;
    /** 当前比分，格式 runs-wickets，如 "77-4" */
    score: {
        /** 展示字符串，如 "120-3" */
        display: string;
    };
    /** 当前 over 进度 */
    over: {
        /** 原始字符串，如 "10.2"（小数点前=已完成Over数，小数点后=本Over已投球数）*/
        display: string;
    };
    /** 追逐目标分数（第二局有值，第一局为 null）*/
    target: number | null;
    /** 剩余合法球数 */
    ballsLeft: number;
    /** 当前得分率 CRR */
    crr: number;
    /** 所需得分率 RRR（仅第二局且非 Test 赛制有值）*/
    rrr: number | null;
    /** 追分提示，如 "DC need 154 runs in 118 balls"（仅第二局有值）*/
    chaseBanner: string | null;
    /** 当前正在进行的 over（current=true 的那个）*/
    currentOver: CricketOverBallsVO | null;
    /** 正常比赛局列表。Test Match 可能存在 3/4 局 */
    innings?: CricketInningVO[] | null;
    /** 加时赛局列表（Super Over），无加时赛时为空或 null */
    superInnings?: CricketInningVO[] | null;
    /** 加时赛状态：IN_PROGRESS / COMPLETED */
    superOverStatus?: "IN_PROGRESS" | "COMPLETED" | null;
    /** 加时赛状态文案，如 "Super Over In Progress" */
    superOverStatusText?: string | null;
    /** 第一局信息 */
    inning1?: CricketInningVO | null;
    /** 第二局信息（第一局未结束时为 null）*/
    inning2?: CricketInningVO | null;
}

/**
 * 获取板球赛事实时比分面板
 * @param eventNo - 我方事件编号
 */
export function apiGetCricketLivePanel( eventNo: string ) {
    return axios.get<{ data: CricketLivePanelVO }>( `/api/front/cricket/matches/${eventNo}/live-panel` );
}
